'use client';

import {
  computePromoMonthlyPrice,
  selectPromoBase,
} from '@/components/pricing/promoPricing';
import { Button } from '@/components/ui/button';
import { PurpleGradientButton } from '@/components/ui/purple-gradient-button';
import { useAuthModal } from '@/contexts/auth-modal-context';
import { useAuth } from '@/hooks/useAuth';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useTierRecommendation } from '@/hooks/useTierRecommendation';
import { customerPortalAction } from '@/lib/payments/actions';
import type { LaunchPromoConfig } from '@/lib/payments/pricing-fetcher';
import { useAppSelector } from '@/lib/redux/hooks';
import { useAppDispatch } from '@/lib/redux/hooks';
import {
  selectIsPricingInitialized,
  selectLaunchPromo,
  selectPlans,
} from '@/lib/redux/selectors/pricingSelectors';
import { closeErrorModal } from '@/lib/redux/slices/errorModalSlice';
import { shouldShowRecommendationBadge } from '@/services/tier-recommendation-service';
import type { Plan, ServerPlan } from '@/types/pricing';
import { Decimal } from 'decimal.js';
import { AnimatePresence, m } from 'framer-motion';
import {
  ArrowRight,
  CreditCard,
  Loader2,
  Package,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { type ReactNode, useState } from 'react';
import {
  type CheckmarkVariant,
  PlanCard,
  TrialCtaSection,
} from './_primitives';
import {
  invoiceFromGross,
  invoiceTotals,
  pricingPageVatView,
} from './pricing-helpers';

// --- PricingCardV2 ------------------------------------------------------------

interface VatDisplayInfo {
  includeVat: boolean;
  vatRate: Decimal | null;
  vatLabel: { EN: string; SK: string };
}

/** next-intl translator bound to the ``PricingPage`` namespace. */
type PricingPageTranslator = ReturnType<typeof useTranslations<'PricingPage'>>;

/**
 * Build a card-ready ``Plan`` from a catalog ``ServerPlan`` — resolves display
 * name, tagline / value-prop, CTA, trial nudge, and localized feature bullets
 * from ``plan_translations`` + ``plan_features`` (3-tier locale → en →
 * fallback). The catalog is authoritative; missing data fails loud (blank slot)
 * rather than silently rendering a stale legacy string.
 *
 * Exported so the Lawyers tab can render the same individual AI cards
 * (Starter/Pro) through PricingCardV2 without re-deriving catalog resolution.
 */
export function buildPlanFromServerPlan(
  plan: ServerPlan,
  t: PricingPageTranslator,
  locale: string
): Plan {
  const keys = plan.translationKeys;

  const safeT = (key: string): string => {
    try {
      return t(key as Parameters<typeof t>[0]);
    } catch {
      return key;
    }
  };

  const catalogTranslation = plan.translations?.[locale];
  const catalogName = catalogTranslation?.display_name;
  const catalogTagline = catalogTranslation?.short_tagline ?? undefined;
  const catalogValueProp = catalogTranslation?.value_proposition ?? undefined;
  const catalogCta = catalogTranslation?.cta_label?.default;

  const catalogFeatures =
    plan.catalogFeatures && plan.catalogFeatures.length > 0
      ? plan.catalogFeatures
          .map((f) => f.translations[locale] ?? f.translations['en'] ?? '')
          .filter((text) => text.length > 0)
      : null;

  return {
    ...plan,
    name: catalogName ?? plan.nameKey,
    priceInfo: keys.priceInfo ? safeT(keys.priceInfo) : '',
    description: catalogValueProp ?? catalogTagline ?? '',
    // ``undefined`` hides the button rather than showing a wrong CTA.
    buttonText: catalogCta,
    trialInfo: keys.trialInfo
      ? t(keys.trialInfo as Parameters<typeof t>[0], { count: 7 })
      : undefined,
    vatInfo: keys.vatInfo ? safeT(keys.vatInfo) : undefined,
    features: catalogFeatures ?? [],
  };
}

interface PricingCardV2Props {
  plan: Plan;
  isAnnual: boolean;
  checkVariant: CheckmarkVariant;
  t: (key: string) => string;
  vatDisplay: VatDisplayInfo;
  locale: string;
  country: string;
  isBusiness: boolean;
  isVatPayer: boolean;
  /** Backend-owned launch-promo config (see LaunchPromoConfig). Drives the
   *  strikethrough + savings-pill display; the actual discount is enforced
   *  at checkout by the backend coupon injection. */
  launchPromo: LaunchPromoConfig;
  /**
   * Optional value-add strip rendered under the price (e.g. "Profil+ listing
   * included free" on the Lawyers tab, where Starter/Pro bundle a directory
   * listing). Presentation-only — the bundle itself is enforced server-side
   * via the ``bundle_professional_profile`` plan capability.
   */
  bundleBadge?: string | undefined;
  /**
   * Personalised recommendation badge derived from the user's actual trial
   * usage ("Odporúčané pre vás"). Only shown when the API returns a
   * recommendation that matches this card's plan tag. Omit to skip.
   */
  recommendationBadge?: string | undefined;
  /** Explanatory tooltip for the recommendation badge ("Based on your trial usage"). */
  recommendationBadgeTooltip?: string | undefined;
}

export function PricingCardV2({
  plan,
  isAnnual,
  checkVariant,
  t,
  vatDisplay,
  locale,
  country,
  isBusiness,
  isVatPayer,
  launchPromo,
  bundleBadge,
  recommendationBadge,
  recommendationBadgeTooltip,
}: PricingCardV2Props) {
  const isFreePlan = plan.price === 0;
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { userPlan, hasActiveSubscription } = useAuthUser();
  const { openSignupModal } = useAuthModal();
  const dispatch = useAppDispatch();

  // Check if user is already on this plan
  const normalizedUserPlan = userPlan?.toLowerCase() ?? '';
  const normalizedPlanId = plan.id.toLowerCase();
  const isCurrentPlan =
    isAuthenticated && normalizedUserPlan === normalizedPlanId;

  const checkoutUrl = `/${locale}/checkout/${btoa(
    JSON.stringify({
      plan: plan.id,
      country,
      is_business: isBusiness,
      is_vat_payer: isVatPayer,
      billing_interval: isAnnual ? 'year' : 'month',
      step: 'address' as const,
    })
  )}`;

  const handleButtonClick = async () => {
    // Don't show loader for instant actions (signup modal, same-page nav)
    if (isFreePlan || isCurrentPlan) {
      if (isFreePlan) {
        isAuthenticated ? router.push('/chat') : openSignupModal();
      } else {
        router.push('/user-profile');
      }
      return;
    }

    if (!isAuthenticated) {
      dispatch(closeErrorModal());
      openSignupModal(checkoutUrl);
      return;
    }

    // Show loader for actions that hit an API (portal session, checkout page)
    setIsLoading(true);
    try {
      dispatch(closeErrorModal());
      if (hasActiveSubscription) {
        await customerPortalAction();
      } else {
        router.push(checkoutUrl);
      }
    } catch {
      setIsLoading(false);
    }
  };

  // Compute VAT-adjusted price
  const grossAmountEur = new Decimal(plan.price).div(100);
  const skVatRate = new Decimal('23');

  // Extract net from SK gross price
  const [, netAmount] = invoiceFromGross(grossAmountEur, skVatRate);

  let displayPrice: number;
  let vatSuffix: string;

  if (isFreePlan) {
    displayPrice = 0;
    vatSuffix = '';
  } else if (vatDisplay.includeVat && vatDisplay.vatRate) {
    // B2C / non-VAT-payer: show gross with customer's VAT rate
    const [, customerGross] = invoiceTotals(netAmount, vatDisplay.vatRate);
    displayPrice = customerGross.toNumber();
    vatSuffix =
      locale === 'sk' ? vatDisplay.vatLabel.SK : vatDisplay.vatLabel.EN;
  } else {
    // B2B VAT payer: show net + "+ DPH"
    displayPrice = netAmount.toNumber();
    vatSuffix =
      locale === 'sk' ? vatDisplay.vatLabel.SK : vatDisplay.vatLabel.EN;
  }

  // Annual pricing overrides (if annual toggle active)
  // Capture the VAT-adjusted monthly price before annual override (for strikethrough display)
  const vatAdjustedMonthlyPrice = displayPrice;
  const monthlyPriceEur = plan.price / 100;
  const annualPriceEur = plan.annual_price_eur;
  const annualMonthlyEquiv =
    annualPriceEur != null ? annualPriceEur / 12 : null;

  if (isAnnual && annualMonthlyEquiv != null && !isFreePlan) {
    // Apply same VAT adjustment as monthly price: extract net from SK gross, then apply customer's VAT
    const annualMonthlyNet = new Decimal(annualMonthlyEquiv).div(
      new Decimal('1.23')
    );
    if (vatDisplay.includeVat && vatDisplay.vatRate) {
      const [, customerGross] = invoiceTotals(
        annualMonthlyNet,
        vatDisplay.vatRate
      );
      displayPrice = customerGross.toNumber();
    } else {
      displayPrice = annualMonthlyNet.toNumber();
    }
  }

  const savingsPct =
    annualPriceEur != null && monthlyPriceEur > 0
      ? Math.round((1 - annualPriceEur / (monthlyPriceEur * 12)) * 100)
      : 0;

  // Launch-promo display (#4744 — Config B).
  // Backend (settings.LAUNCH_PROMO_ACTIVE + LAUNCH_PROMO_PLANS + the per-plan
  // discount map in launch_promo_service) is the single source of truth —
  // /internal/pc returns the active/plans/discount-pct payload, this card
  // just renders what it gets. The actual discount is enforced at checkout
  // by the backend coupon; this is purely the marketing presentation layer.
  const planDiscount = launchPromo.active
    ? launchPromo.plans[plan.id.toLowerCase()]
    : undefined;
  const promoSavingsPctRaw = planDiscount
    ? isAnnual
      ? planDiscount.year_pct
      : planDiscount.month_pct
    : null;
  const isPromoEligible =
    !isFreePlan && promoSavingsPctRaw != null && promoSavingsPctRaw > 0;
  const promoSavingsPct = isPromoEligible ? promoSavingsPctRaw! : 0;

  // promoBase = the price the promo % is taken off. ``selectPromoBase`` pins
  // this to the MONTHLY headline (``vatAdjustedMonthlyPrice`` = 27 € Starter /
  // 59 € Pro / 4,90 € Profil+) for BOTH cadences, NOT the annual-monthly-
  // equivalent (``displayPrice``, e.g. 270/12 = 22,50 € on Annual). On Annual
  // the equivalent already bakes in the ~17% annual discount, so discounting it
  // again double-discounts and shows e.g. Starter at 9 € (= 60% off 22,50)
  // instead of the intended 10,80 € (= 60% off 27). Basing it on the monthly
  // headline matches the actual checkout charge (the coupon applies to the full
  // 12× monthly base, #4965) and keeps the strikethrough (27 €) and the −% pill
  // mutually consistent (10,80 = 60% off 27). Routing through the selector (vs
  // inlining ``vatAdjustedMonthlyPrice``) is the fenced wiring (#5011): a
  // regression to the annual-equiv base fails ``selectPromoBase``'s unit test.
  const promoBase = selectPromoBase(vatAdjustedMonthlyPrice, displayPrice);
  // promoDisplayPrice = the final discounted per-month price (Decimal arithmetic
  // avoids float drift; truncated to 2 dp, no round-up).
  const promoDisplayPrice = isPromoEligible
    ? computePromoMonthlyPrice(promoBase, promoSavingsPct)
    : null;
  // promoStrikethrough = the same base the promo % is taken off, so the struck
  // price, the discounted price, and the pill all agree on Annual as well as
  // Monthly.
  const effectiveDisplayPrice =
    promoDisplayPrice !== null ? promoDisplayPrice : displayPrice;
  const promoStrikethrough = promoDisplayPrice !== null ? promoBase : undefined;

  // Build the footer (the CTA). Four variants, all product-specific —
  // stays in the caller rather than getting baked into PlanCard.
  let footer: ReactNode = null;
  if (plan.buttonText) {
    if (isCurrentPlan) {
      footer = (
        <Button
          variant='outline'
          className='w-full min-h-[48px] text-sm font-semibold border-line-subtle text-content-muted rounded-lg cursor-default'
          disabled
        >
          {t('currentPlan')}
        </Button>
      );
    } else if (isAuthenticated && hasActiveSubscription && !isFreePlan) {
      footer = (
        <Button
          variant='outline'
          className='w-full min-h-[48px] text-sm font-semibold border-brand-tertiary text-brand-tertiary hover:bg-brand-tertiary/10 rounded-lg'
          onClick={handleButtonClick}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            t('changePlan')
          )}
        </Button>
      );
    } else if (plan.buttonVariant === 'outline') {
      footer = (
        <Button
          variant='outline'
          className='w-full min-h-[48px] text-sm font-semibold border-line-strong text-content-primary hover:bg-interactive-hover rounded-lg'
          onClick={handleButtonClick}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            plan.buttonText
          )}
        </Button>
      );
    } else {
      footer = (
        <PurpleGradientButton
          withShimmer={
            !isLoading && plan.isPopular ? (plan.callOut ?? false) : false
          }
          className='w-full min-h-[48px] text-sm'
          rightIcon={
            isLoading ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <ArrowRight className='h-4 w-4' aria-hidden='true' />
            )
          }
          onClick={handleButtonClick}
          disabled={isLoading}
        >
          {plan.buttonText}
        </PurpleGradientButton>
      );
    }
  }

  return (
    <PlanCard
      name={plan.name}
      // ``Plan.description`` is non-optional but may be '' (the ?? '' fallback
      // in buildPlanFromServerPlan). Coerce empty → undefined so PlanCard skips
      // the paragraph rather than rendering an empty <p>.
      description={plan.description || undefined}
      trialInfo={plan.trialInfo}
      recommendationBadge={recommendationBadge}
      recommendationBadgeTooltip={recommendationBadgeTooltip}
      bundleBadge={bundleBadge}
      isPopular={plan.isPopular}
      popularLabel={t('mostPopular')}
      checkVariant={checkVariant}
      features={plan.features}
      priceDisplay={{
        freeLabel: isFreePlan ? t('freePrice') : undefined,
        amount: isFreePlan ? undefined : effectiveDisplayPrice,
        currency: isFreePlan ? undefined : 'EUR',
        periodLabel: isFreePlan ? undefined : t('perMonth'),
        // Promo strikethrough takes priority over the annual-vs-monthly one.
        strikethrough: isPromoEligible
          ? promoStrikethrough
          : isAnnual && !isFreePlan && annualMonthlyEquiv != null
            ? vatAdjustedMonthlyPrice
            : undefined,
        savingsPct: isPromoEligible
          ? promoSavingsPct
          : isAnnual &&
              !isFreePlan &&
              annualMonthlyEquiv != null &&
              savingsPct > 0
            ? savingsPct
            : undefined,
        cadenceLabel: isFreePlan
          ? undefined
          : isAnnual && annualMonthlyEquiv != null
            ? t('billedAnnually')
            : t('billedMonthly'),
        vatSuffix: vatSuffix || undefined,
      }}
      footer={footer}
    />
  );
}

// --- TrustSignals ------------------------------------------------------------

function TrustSignals({ t }: { t: (key: string) => string }) {
  const items = [
    { Icon: Shield, label: t('trustFreeTrial') },
    { Icon: CreditCard, label: t('trustNoCard') },
    { Icon: RefreshCw, label: t('trustCancel') },
  ] as const;

  return (
    <div className='mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3'>
      {items.map(({ Icon, label }) => (
        <div
          key={label}
          className='flex items-center gap-2 text-sm text-content-secondary'
        >
          <Icon
            className='h-4 w-4 text-content-muted flex-shrink-0'
            aria-hidden='true'
          />
          {label}
        </div>
      ))}
    </div>
  );
}

// --- Main export -------------------------------------------------------------

// Keyed by ``plans.tag`` (lowercase — the only plan identifier; see CLAUDE.md
// "Plan catalog"). ``plan.id`` carries the tag verbatim, so the lookup must use
// lowercase keys — Title-case keys (the prior bug, #5019) never matched and
// every card silently fell back to ``info``.
export const CHECK_VARIANTS: Record<string, CheckmarkVariant> = {
  trial: 'muted',
  starter: 'info',
  plus: 'brand',
  prof_profile: 'info',
};

interface PricingV2SectionProps {
  country?: string;
  isBusiness?: boolean;
  isVatPayer?: boolean;
  /** Billing cadence — controlled by parent PricingSwitcher */
  isAnnual: boolean;
  /** Clicking the "Already subscribed? Buy a pack" link routes here */
  onSwitchToPack: () => void;
}

export function PricingV2Section({
  country = 'SK',
  isBusiness = true,
  isVatPayer = true,
  isAnnual,
  onSwitchToPack,
}: PricingV2SectionProps) {
  const reduxPlans = useAppSelector(selectPlans);
  const isPricingInitialized = useAppSelector(selectIsPricingInitialized);
  const launchPromo = useAppSelector(selectLaunchPromo);
  const t = useTranslations('PricingPage');
  const locale = useLocale();
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const { openSignupModal } = useAuthModal();

  // Trial CTA handler — same shape as the free-plan handler used inside
  // PricingCardV2: anonymous users get the signup modal, authenticated users
  // jump straight to /chat where the trial requests are consumed.
  const handleStartTrial = () => {
    if (isAuthenticated) {
      router.push(`/${locale}/chat`);
    } else {
      openSignupModal();
    }
  };

  // Fetch personalised tier recommendation derived from the user's trial usage.
  // Unauthenticated visitors get null — no badge shows, pricing page is unaffected.
  const { recommendation } = useTierRecommendation();

  const vatDisplay = pricingPageVatView(country, isBusiness, isVatPayer);

  if (!isPricingInitialized) {
    return (
      <div className='flex justify-center items-center min-h-[300px]'>
        <div className='text-sm text-content-muted'>{t('loading')}</div>
      </div>
    );
  }

  const v2Plans = reduxPlans.filter((p) => p.plan_type != null);
  if (v2Plans.length === 0) return null;

  const individualServerPlans = v2Plans.filter(
    (p) => p.plan_type === 'individual' && p.price > 0
  );

  // Translate ServerPlan -> Plan, sourcing plan-card copy from the catalog.
  //
  // Phase C closure: ``display_name``, ``value_proposition`` and
  // ``cta_label`` are now backfilled for every visible plan, so the legacy
  // ``messages/{en,sk}.json`` key fallback for those slots was removed —
  // the catalog is authoritative and missing data fails loud (blank slot)
  // rather than silently rendering a stale legacy string. Catalog resolution
  // lives in the shared ``buildPlanFromServerPlan`` (reused by the Lawyers tab).
  const planRows = individualServerPlans.map((p) =>
    buildPlanFromServerPlan(p, t, locale)
  );

  // Grid sizes itself to however many individual paid tiers exist today
  // (Starter/Pro/Kancelária = 3) instead of a number hardcoded to whatever
  // was true when this was last written — that assumption broke silently
  // when Kancelária shipped (#pricing-tier-redesign).
  const gridCols =
    planRows.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2';
  const gridMaxWidth = planRows.length >= 3 ? 'max-w-5xl' : 'max-w-3xl';

  return (
    <section
      id='pricing-v2'
      aria-labelledby='pricing-v2-heading'
      className='w-full'
    >
      {/* Visually-hidden region label. The individual-plans grid has no
          visible heading of its own (the visible audience label lives in the
          parent AudienceSwitcher), so this h2 gives the labelled region an
          accessible name — and is the id ``aria-labelledby`` points at (the
          section id is ``pricing-v2``, not its own label — #5083). */}
      <h2 id='pricing-v2-heading' className='sr-only'>
        {t('individualPlansHeading')}
      </h2>

      {/* Pricing cards */}
      <m.div
        layout
        transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
        className={`${gridMaxWidth} mx-auto grid grid-cols-1 ${gridCols} gap-6`}
      >
        <AnimatePresence mode='popLayout' initial={false}>
          {planRows.map((plan) => (
            <m.div
              key={plan.id}
              layout
              className='h-full'
              initial={{ opacity: 0, x: -24, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -24, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            >
              <PricingCardV2
                plan={plan}
                isAnnual={isAnnual}
                checkVariant={CHECK_VARIANTS[plan.id.toLowerCase()] ?? 'info'}
                vatDisplay={vatDisplay}
                locale={locale}
                country={country}
                isBusiness={isBusiness}
                isVatPayer={isVatPayer}
                launchPromo={launchPromo}
                // Personalised recommendation badge (#5554): show only on the
                // card whose plan tag matches the user's recommendation, and
                // only when the user actually has trial usage behind it. The
                // section→card mapping is the pure ``shouldShowRecommendationBadge``
                // helper so it is locked by a behavioural unit test rather than
                // a source-string grep (#5563).
                recommendationBadge={
                  shouldShowRecommendationBadge(recommendation, plan.id)
                    ? t('recommendedForYou')
                    : undefined
                }
                recommendationBadgeTooltip={t('recommendedForYouTooltip')}
                t={(key) => {
                  try {
                    return t(key as Parameters<typeof t>[0]);
                  } catch {
                    return key;
                  }
                }}
              />
            </m.div>
          ))}
        </AnimatePresence>
      </m.div>

      {/* Free-trial section — rich CTA below the paid tier grid (Maros
          feedback 2026-05-05). All copy is catalog-driven (plan_translations
          + plan_features for the trial plan tag). Extracted into the shared
          TrialCtaSection primitive so the Lawyers tab can render the same
          block without duplicating the derivation logic. */}
      <TrialCtaSection
        plans={v2Plans}
        locale={locale}
        onStartTrial={handleStartTrial}
      />

      {/* Soft rail — lets existing customers jump to packs without
          competing with the primary subscribe CTA above. */}
      <div className='mt-6 flex justify-center'>
        <button
          type='button'
          onClick={onSwitchToPack}
          className='inline-flex items-center gap-1.5 text-sm text-brand-tertiary hover:text-brand-tertiary/80 transition-colors'
        >
          <Package className='h-3.5 w-3.5' aria-hidden='true' />
          {t('alreadyHavePlanLink')}
        </button>
      </div>

      {/* Trust signals */}
      <TrustSignals
        t={(key) => {
          try {
            return t(key as Parameters<typeof t>[0]);
          } catch {
            return key;
          }
        }}
      />
    </section>
  );
}
